import { MissingComponentWarning } from "core/MissingComponentWarning";
import React, { FunctionComponent } from "react";
import { ReportStatus, ReportType } from "api/model/enums";
import { objectPropertiesToArray } from "../utils/utils";
import { ComponentTypeMappings } from "./component-mappings";
import { IProductTemplateTableProps } from "./dashboard/product-template-table/ProductTemplateTable";
import { IPeriodLoadingConfig, LoadingConfig } from "./dashboard/product-template-table/utils/interfaces";
import { DatasetLoadingConf } from "./dashboard/charts/interfaces";
import { IComponentTimed } from "./pages/dashboard/components/interfaces";

interface IComponentType extends Object {
  name: String;
  imageUrl?: String;
  dashboardComponent?: boolean;
  supportsStatusCallback?: boolean;
  invisible?: boolean;
  entryPoint: Function;
  editorEntryPoint?: FunctionComponent<DashboardEditorProps<any>>;
  editorConfig?: EditorConfig;
}

export type ComponentTypeWithID = IComponentType & IdType;

type EditorConfig = {
  hasTitle: boolean;
};

export interface DashboardEditorProps<T> {
  config: T;
  onConfigChange: (newConfig: Partial<T>) => void;
}

export interface IProductTemplateTableEditor {
  id?: number;
  safeName: string;
  transposed?: boolean;
  showTitle?: boolean;
  updateFrequency?: number;
  hiddenColumns?: Array<string>;
  onlyShownColumns?: Array<string>;
  itemsPerPage?: number;
  overrideLoadingConfig?: LoadingConfig;
  overriddenColumnValues?: OverriddenColumnValues;
  filter?: string | null;
  defaultFilter?: string;
  hideDates?: boolean;
  showFilter?: boolean;
  allowAddEntry?: boolean;
  allowRowEdit?: boolean;
  showTotals?: boolean;
  hidePagination?: boolean;
}

export interface OverriddenColumnValues {
  selectedColumnsToOverride: Array<SelectedColumnToOverride>;
  overrideColumnValues: boolean;
}

interface SelectedColumnToOverride {
  columnTitle: string;
  defaultValue: string;
}

export interface IGanttChartEditor extends IProductTemplateTableEditor {
  ganttRanges: IGanttRange[];
  ganttTimestamps: IGanttTimestamp[];
  height?: number;
  showLegends?: boolean;
  aggregateY?: boolean;
  columnOverrideYLabels: string;
  isRelative?: boolean;
  valueColumn?: string;
  startColumn?: string;
  endColumn?: string;
  loadConf?: DatasetLoadingConf;
}

export interface IGanttRangeRelative {
  id: string;
  valueInEntry: string;
  displayValue: string;
  color: string;
  startDateColumn: string | null;
  endDateColumn: string | null;
}

export interface IGanttRange extends IGanttRangeRelative {
  id: string;
  startDateColumn: string | null;
  endDateColumn: string | null;
  title: string | null;
  color: string;
}

export interface IGanttTimestamp {
  id: string;
  dateColumn: string | null;
  icon: string | null;
  title: string | null;
  color: string;
}

export type GanttChartConfigProps = {
  currentConfig: IGanttChartEditor;
  setCurrentConfig: React.Dispatch<React.SetStateAction<IGanttChartEditor>>;
};

export type GanttProps = IProductTemplateTableProps & IGanttChartEditor;

export type GeneratedReportTableConfig = {
  ids: number[];
  itemsPerPage?: number;
  type?: ReportType;
  showDateSelection?: boolean;
  loadingConfig?: IPeriodLoadingConfig;
  emailRecipients?: string[];
  showEmailButton?: boolean;
};

export type GeneratedReportTableProps = {
  sort?: "ASC" | "DESC";
  referenceTimePattern?: string;
  status?: ReportStatus;
  hideHeader?: boolean;
  superUserComponent?: boolean;
} & IComponentTimed & GeneratedReportTableConfig;

export interface IComponentTypeMapping {
  [key: string]: IComponentType;
}

export function loadComponentModule(type: keyof IComponentTypeMapping) {
  if (!(type in ComponentTypeMappings)) {
    console.warn(`Can't map unknown component type: ${type}`);
    return <MissingComponentWarning componentId={type as string} />;
  }
  return ComponentTypeMappings[type].entryPoint();
}

export function loadEditorModule(type: keyof IComponentTypeMapping) {
  if (!(type in ComponentTypeMappings)) {
    console.warn(`Can't map unknown component type: ${type}`);
    return undefined;
  }
  return ComponentTypeMappings[type].editorEntryPoint;
}

export function getComponentType(type: keyof IComponentTypeMapping): IComponentType | undefined {
  if (!(type in ComponentTypeMappings)) {
    return undefined;
  }

  return ComponentTypeMappings[type];
}

export function getDashboardComponentTypes(): ComponentTypeWithID[] {
  return objectPropertiesToArray(ComponentTypeMappings).filter(
    type => type.dashboardComponent
  ) as ComponentTypeWithID[];
}
